import React, { useState, useEffect, useRef } from 'react';
import LeftSidebar from '../Tools/LeftSidebar';
import Topbar from '../Tools/Topbar';
import Pagination from '../Tools/Pagination';
import AddUserModal from '../tabs/modals/UserModal/AddUserModal';
import Settings from '../Tools/Setting/Settings';
import useSettings from '../Tools/Setting/useSettings';
import api from '../../../api';

const UserManagement = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const { settings, updateSetting, handleColorTheme } = useSettings();
  const settingsRef = useRef();

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 4;

  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    username: '',
    phone: '',
    email: '',
    password: '',
    address: '',
    role: '',
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const getUsers = async () => {
    try {
      const res = await api.get('/users-management');
      setUsers(res.data.data || res.data);
    } catch (error) {
      alert('Failed to fetch users');
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      setNewUser({ ...newUser, [name]: digitsOnly });
    } else {
      setNewUser({ ...newUser, [name]: value });
    }
  };

  const handleAddUser = async () => {
  try {
    // ✅ Validate only during Add (not Edit)
    if (!isEditMode) {
      const requiredFields = ['first_name', 'email', 'password', 'role'];
      const missingField = requiredFields.find((field) => !newUser[field] || newUser[field].trim() === '');

      if (missingField) {
        alert(`Please fill the ${missingField.replace('_', ' ')} field!`);
        return;
      }
    }

    // ✅ Prepare payload
    const payload = {
      ...newUser,
      role_id: parseInt(newUser.role),
      phone: parseInt(newUser.phone),
    };
    delete payload.role;

    // ✅ Remove password if blank (edit case only)
    if (isEditMode && (!newUser.password || newUser.password.trim() === '')) {
      delete payload.password;
    }

    // ✅ Phone number check
    if (isNaN(payload.phone)) {
      alert('Please enter a valid phone number (digits only)');
      return;
    }

    if (isEditMode && selectedUserId) {
      await api.put(`/update-user/${selectedUserId}`, payload);
      alert('User updated successfully!');
    } else {
      await api.post('/add-new-user', payload);
      alert('User added successfully!');
    }

    getUsers();
    document.getElementById('closeAddUserModal')?.click();
    resetForm();
  } catch (error) {
    console.error(error?.response?.data || error.message);
    alert(
      error?.response?.data?.message || (isEditMode ? 'Update failed!' : 'Add user failed!')
    );
  }
};


  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/delete-user/${id}`);
        alert('User deleted successfully!');
        getUsers();
      } catch (error) {
        alert('Delete user failed!');
      }
    }
  };


  const resetForm = () => {
    setNewUser({
      first_name: '',
      last_name: '',
      username: '',
      phone: '',
      email: '',
      password: '',
      address: '',
      role: '',
    });
    setIsEditMode(false);
    setSelectedUserId(null);
  };

  const paginatedUsers = users.slice((page - 1) * limit, page * limit);
  const total = users.length;

  return (
    <div id="main-wrapper">
      <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <Topbar toggleSidebar={toggleSidebar} />
        <div className="body-wrapper">
          <div className="container-fluid">
           <Settings 
            ref={settingsRef}
            updateSetting={updateSetting}
            handleColorTheme={handleColorTheme}
            settings={settings}  // Add this line
          />

            <div className="card w-100 position-relative overflow-hidden">
              <div className="px-4 py-3">
                <h5 className="card-title fw-semibold mb-0 lh-sm">User Management</h5>
                <p className="card-subtitle mb-0 mt-1">
                  Centralized control for user profiles, roles, and login status.
                </p>
              </div>

              <div className="card-body p-4">
                <div className="filter-search mt-5">
                  <h6 className="px-2 mb-0">Filter</h6>
                  <div className="filter-panel">
                    <form>
                      <div className="form-group col-lg-6 p-2">
                        <input type="text" className="form-control" placeholder="Search" />
                      </div>
                      <div className="form-group col-lg-6 p-2">
                        <select className="form-control form-select">
                          <option>Select Role</option>
                          <option>Admin</option>
                          <option>Resource Manager</option>
                          <option>End User</option>
                        </select>
                      </div>
                    </form>

                    <button
                      className="btn m-2 d-flex align-items-center justify-content-center btn-outline-primary"
                      onClick={() => {
                        resetForm();
                        const modal = new window.bootstrap.Modal(document.getElementById('bs-user'));
                        modal.show();
                      }}
                    >
                      <i className="ti ti-circle-plus fs-4 me-2"></i> Add User
                    </button>
                  </div>
                </div>

                <div className="py-3 px-0">
                  <div className="table-responsive rounded-2 ros-table">
                    <table className="table text-nowrap customize-table mb-0 align-middle">
                      <thead className="text-dark fs-4">
                        <tr>
                          <th>User ID</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedUsers.map((user) => (
                          <tr key={user.id}>
                            <td>{user.id}</td>
                            <td>{user.first_name} {user.last_name}</td>
                            <td>{user.email}</td>
                            <td>{user.role?.name || 'N/A'}</td>
                            <td>
                              <span className={`round-8 rounded-circle me-2 d-inline-block ${
                                user.is_active === 1 ? 'text-bg-success' : 'text-bg-danger'
                              }`}></span>
                              {user.is_active === 1 ? 'Active' : 'Inactive'}
                            </td>
                            <td>
                              <a
                                
                                className="h4 me-3 text-warning"
                                onClick={() => {
                                  setNewUser({
                                    first_name: user.first_name,
                                    last_name: user.last_name,
                                    username: user.username,
                                    phone: String(user.phone),
                                    email: user.email,
                                    password: '',
                                    address: user.address,
                                    role: user.role_id || '',
                                  });
                                  setIsEditMode(true);
                                  setSelectedUserId(user.id);
                                  setTimeout(() => {
                                    const modal = new window.bootstrap.Modal(document.getElementById('bs-user'));
                                    modal.show();
                                  }, 100);
                                }}
                              >
                                <i className="ti ti-edit"></i>
                              </a>
                              <a
                                className="h4 text-danger"
                                onClick={() => handleDelete(user.id)}
                              >
                                <i className="ti ti-trash"></i>
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <Pagination
                  total={total}
                  limit={limit}
                  page={page}
                  onPageChange={setPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddUserModal
        newUser={newUser}
        handleInputChange={handleInputChange}
        handleAddUser={handleAddUser}
        isEditMode={isEditMode}
      />

      <div className="dark-transparent sidebartoggler" onClick={toggleSidebar}></div>
    </div>
  );
};

export default UserManagement;
